import React, { useEffect, useState } from "react";
import { Mail, RefreshCcw, Users } from "lucide-react";
import { API_BASE_URL } from "../api";
import { Button, MetricCard, SectionHeader, SurfaceCard } from "../components/designSystem";

function AdminSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSubscribers = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Admin access required.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    fetch(`${API_BASE_URL}/api/newsletter/subscribers`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setSubscribers(data);
        } else {
          setError(data.error || "Failed to fetch subscribers.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Network error.");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSubscribers();
  }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.16),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Subscribers"
          title="Newsletter subscriber list"
          description="Review the email addresses currently subscribed to your newsletter."
          action={
            <Button type="button" variant="secondary" onClick={loadSubscribers}>
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
          }
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Subscribers" value={String(subscribers.length)} helper="Current newsletter audience size." />
          <MetricCard label="Status" value={loading ? "Loading" : "Ready"} helper="Live fetch state for this page." />
          <MetricCard label="Source" value="Backend" helper="Results come directly from the admin endpoint." />
        </div>
      </SurfaceCard>

      {error ? <SurfaceCard><p className="text-sm font-medium text-rose-600 dark:text-rose-300" role="alert">{error}</p></SurfaceCard> : null}

      {loading ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading subscribers...</p>
        </SurfaceCard>
      ) : subscribers.length === 0 ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">No subscribers found.</p>
        </SurfaceCard>
      ) : (
        <div className="space-y-4">
          {subscribers.map((entry) => (
            <SurfaceCard key={entry._id}>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-900 dark:bg-gray-900 dark:text-white">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-semibold text-slate-950 dark:text-white">{entry.email}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Subscribed {new Date(entry.subscribedAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-slate-400">
                  <Users className="h-3.5 w-3.5" />
                  Subscriber
                </div>
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminSubscribers;
